import {inject, Injectable} from "@angular/core"
import {Router} from "@angular/router"
import {ContentTypeModel, NextActor, OrganizationType, PaymentState, PictureState} from "@generated"
import {AuthService} from "@common/services/auth/auth.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {SiloService} from "@common/services/auth/silo.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {createLinkAddTemplateRevisionFromPictures} from "@common/helpers/routes"
import {AppRoutes} from "@app/platform/config/navigation-config"
import {
    PictureCreationGetDefaultProjectGQL,
    PictureCreationCreateProjectGQL,
    PictureCreationCreateSetGQL,
    PictureCreationCreatePictureGQL,
    PictureCreationCreatePictureMutation,
} from "@app/common/services/picture-creation/picture-creation.generated"

export interface PictureCreationOptions {
    setId?: string
    projectId?: string
    newTab?: boolean
}

export interface PictureCreationResult {
    picture: PictureCreationCreatePictureMutation["createPicture"]
    setId: string
    projectId?: string
}

@Injectable({providedIn: "root"})
export class PictureCreationService {
    private readonly router = inject(Router)
    private readonly auth = inject(AuthService)
    private readonly silo = inject(SiloService)
    private readonly notifications = inject(NotificationsService)
    private readonly refresh = inject(RefreshService)
    private readonly organizations = inject(OrganizationsService)
    private readonly getDefaultProjectGql = inject(PictureCreationGetDefaultProjectGQL)
    private readonly createProjectGql = inject(PictureCreationCreateProjectGQL)
    private readonly createSetGql = inject(PictureCreationCreateSetGQL)
    private readonly createPictureGql = inject(PictureCreationCreatePictureGQL)

    /**
     * Creates a picture and navigates to the appropriate destination.
     * For EnterpriseCloud orgs, navigates to add template revision.
     * For others, navigates to the pictures list.
     */
    async createPictureAndNavigate(options?: PictureCreationOptions): Promise<void> {
        const result = await this.createPicture(options)
        if (!result) {
            if (options?.newTab) {
                const url = this.router.serializeUrl(
                    this.router.createUrlTree([AppRoutes.Home], {
                        queryParams: {tab: "projects"},
                        queryParamsHandling: "merge",
                    }),
                )
                window.open(url, "_blank")
            } else {
                await this.router.navigate([AppRoutes.Home], {
                    queryParams: {tab: "projects"},
                    queryParamsHandling: "merge",
                })
            }
            return
        }

        if (this.organizations.$current()?.type === OrganizationType.EnterpriseCloud) {
            if (options?.newTab) {
                const url = this.router.serializeUrl(
                    this.router.createUrlTree(createLinkAddTemplateRevisionFromPictures(result.picture.id), {
                        queryParams: {tab: "projects", setId: result.setId, projectId: null},
                        queryParamsHandling: "merge",
                    }),
                )
                window.open(url, "_blank")
            } else {
                await this.router.navigate(createLinkAddTemplateRevisionFromPictures(result.picture.id), {
                    queryParams: {tab: "projects", setId: result.setId, projectId: null},
                    queryParamsHandling: "merge",
                })
            }
        } else {
            if (options?.newTab) {
                const url = this.router.serializeUrl(
                    this.router.createUrlTree([AppRoutes.Home], {
                        queryParams: {tab: "projects", setId: result.setId, projectId: null},
                        queryParamsHandling: "merge",
                    }),
                )
                window.open(url, "_blank")
            } else {
                await this.router.navigate([AppRoutes.Home], {
                    queryParams: {tab: "projects", setId: result.setId, projectId: null},
                    queryParamsHandling: "merge",
                })
            }
        }
    }

    /**
     * Creates a picture in the specified set, or auto-selects/creates the set if not provided.
     * Returns the created picture and set information, or null if creation failed.
     */
    async createPicture(options?: PictureCreationOptions): Promise<PictureCreationResult | null> {
        let setId = options?.setId
        let projectId = options?.projectId

        // Auto-select or create set if needed
        if (!setId) {
            const result = await this.resolveOrCreateSet(projectId)
            if (!result) {
                return null
            }
            setId = result.setId
            projectId = result.projectId
        }

        try {
            const {createPicture: picture} = await mutateThrowingErrors(this.createPictureGql)({
                input: {
                    setId,
                    state: PictureState.Draft,
                    paymentState: PaymentState.OrderPlaced,
                    nextActor: this.auth.$actingAsCustomer() ? NextActor.Customer : NextActor.Team1,
                },
            })
            this.refresh.contentTypeModel(ContentTypeModel.Picture)
            this.notifications.showInfo("Picture created.")
            return {picture, setId, projectId}
        } catch {
            this.notifications.showError("Cannot create picture.")
            return null
        }
    }

    private async resolveOrCreateSet(projectId?: string): Promise<{setId: string; projectId?: string} | null> {
        const systemRole = this.silo.$silo()?.systemRole
        if (systemRole) {
            this.notifications.showInfo("Select a Set/Subfolder in the Projects tab before adding a picture.")
            return null
        }

        const organizationId = this.auth.silo.$silo()?.organization?.id
        if (!organizationId) {
            this.notifications.showInfo("Select a Set/Subfolder in the Projects tab before adding a picture.")
            return null
        }

        try {
            const projects = await fetchThrowingErrors(this.getDefaultProjectGql)({organizationId})
            const defaultProject = projects.organization.projects?.[0]
            const defaultSetId = defaultProject?.sets?.[0]?.id

            if (defaultSetId) {
                return {setId: defaultSetId, projectId: defaultProject?.id}
            }

            // No set exists - create project and/or set
            return await this.createDefaultProjectAndSet(organizationId, projectId ?? defaultProject?.id)
        } catch {
            this.notifications.showError("Cannot load projects.")
            return null
        }
    }

    private async createDefaultProjectAndSet(organizationId: string, existingProjectId?: string): Promise<{setId: string; projectId: string} | null> {
        try {
            let projectId = existingProjectId

            if (!projectId) {
                // Create a default project if none exists
                const {createProject: project} = await mutateThrowingErrors(this.createProjectGql)({
                    input: {
                        name: "Default Project",
                        organizationId,
                    },
                })
                projectId = project.id
            }

            // Create a default set within the project
            const {createSet: set} = await mutateThrowingErrors(this.createSetGql)({
                input: {
                    name: "Default Set",
                    projectId,
                },
            })

            return {setId: set.id, projectId}
        } catch {
            this.notifications.showError("Cannot create default project and set.")
            return null
        }
    }
}
